import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['emailNotice']
  static values = { messages: Object }

  connect() {
    this.connection = Symbol('connection')
    this.queue = new Map()
    this.failed = new Set()
    this.active = null
    this.feedbackTimers = new Map()
    this.fields.forEach(field => {
      if (!field.dataset.profileAutosaveSaved) this.remember(field, this.value(field))
      this.feedbackContainer(field)
    })
  }

  disconnect() {
    this.connection = null
    this.queue.clear()
    this.feedbackTimers.forEach(timer => clearTimeout(timer))
    this.feedbackTimers.clear()
  }

  get fields() {
    return Array.from(this.element.elements).filter(field =>
      /^user\[[^\]]+\]$/.test(field.name) &&
      !['hidden', 'password', 'submit', 'button'].includes(field.type) &&
      !field.readOnly && !field.matches(':disabled')
    )
  }

  attribute(field) { return field.name.slice(5, -1) }
  value(field) { return field.type === 'checkbox' ? field.checked : field.value }
  saved(field) { return JSON.parse(field.dataset.profileAutosaveSaved) }
  remember(field, value) { field.dataset.profileAutosaveSaved = JSON.stringify(value) }

  input(event) {
    const field = event.target
    if (!this.fields.includes(field)) return
    const pending = this.value(field) !== this.saved(field) || this.active?.field === field || this.queue.has(field) || this.failed.has(field)
    this.feedback(field, pending ? this.messagesValue.pending : '')
  }

  change(event) {
    if (this.fields.includes(event.target)) this.enqueue(event.target)
  }

  submit(event) {
    event.preventDefault()
    this.fields.forEach(field => this.enqueue(field))
  }

  enqueue(field) {
    const value = this.value(field)
    const inFlight = this.active?.field === field
    if (!inFlight && value === this.saved(field) && !this.failed.has(field)) {
      this.queue.delete(field)
      this.feedback(field, '')
      return
    }
    if (!field.checkValidity()) {
      this.queue.delete(field)
      this.failed.add(field)
      this.feedback(field, field.validationMessage, true)
      return
    }
    this.queue.set(field, value)
    this.feedback(field, this.messagesValue.pending)
    this.drain()
  }

  async drain() {
    if (this.active || !this.connection || !this.queue.size) return
    const [field, value] = this.queue.entries().next().value
    this.queue.delete(field)
    if (value === this.saved(field) && !this.failed.has(field)) return this.drain()

    const connection = this.connection
    const attribute = this.attribute(field)
    const user = { [attribute]: value }
    if (attribute === 'birthdate' && this.element.elements.namedItem('user[birthdate_is_estimate]')) {
      user.birthdate_is_estimate = false
    }
    this.active = { field, value }
    this.failed.delete(field)
    this.feedback(field, this.messagesValue.saving, false, 'saving')
    try {
      const response = await fetch(this.element.action, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || this.element.elements.namedItem('authenticity_token')?.value || ''
        },
        body: JSON.stringify({ user })
      })
      const data = await response.json()
      if (connection !== this.connection) return
      if (!response.ok || !data.success) {
        const errors = data.field_errors?.[attribute] || data.errors || []
        this.failure(field, Array.isArray(errors) ? errors.join('. ') : this.messagesValue.error)
      } else {
        const canonical = data.values && Object.prototype.hasOwnProperty.call(data.values, attribute) ? data.values[attribute] : value
        const savedValue = field.type === 'checkbox' ? Boolean(canonical) : String(canonical ?? '')
        this.remember(field, savedValue)
        if (this.value(field) === value) {
          this.clearServerErrors(field)
          if (field.type === 'checkbox') field.checked = savedValue
          else field.value = savedValue
          this.feedback(field, this.messagesValue.saved, false, 'saved')
        } else if (!this.failed.has(field)) {
          this.feedback(field, this.messagesValue.pending)
        }
        if (attribute === 'email' && this.hasEmailNoticeTarget && data.email_notice_html !== undefined) {
          this.emailNoticeTarget.innerHTML = data.email_notice_html
        }
      }
    } catch (_) {
      if (connection === this.connection) this.failure(field, this.messagesValue.error)
    } finally {
      if (connection === this.connection) {
        this.active = null
        this.drain()
      }
    }
  }

  failure(field, message) {
    this.failed.add(field)
    this.feedback(field, message || this.messagesValue.error, true)
  }

  feedbackContainer(field) {
    const id = `${field.id}_autosave`
    let container = this.element.querySelector(`#${id}`)
    if (!container) {
      container = document.createElement('div')
      container.id = id
      container.className = 'profile-autosave-feedback form-text'
      container.dataset.profileAutosaveFeedback = ''
      container.setAttribute('aria-live', 'polite')
      container.setAttribute('role', 'status')
      container.setAttribute('aria-atomic', 'true')
      const anchor = field.closest('.input-group, .form-check') || field
      anchor.insertAdjacentElement('afterend', container)
    }
    const descriptions = new Set((field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean))
    descriptions.add(id)
    field.setAttribute('aria-describedby', Array.from(descriptions).join(' '))
    return container
  }

  feedback(field, message, invalid = false, state = 'pending') {
    const container = this.feedbackContainer(field)
    const nextState = invalid ? 'error' : (message ? state : 'idle')
    if (container.dataset.state === nextState && container.querySelector('span')?.textContent === (message || '')) return
    clearTimeout(this.feedbackTimers.get(field))
    this.feedbackTimers.delete(field)
    container.dataset.state = nextState
    container.replaceChildren()
    if (message) {
      const icon = document.createElement('i')
      const iconName = invalid ? 'exclamation-circle' : ({ saving: 'arrow-repeat', saved: 'check2', pending: 'circle' }[state])
      icon.className = `bi bi-${iconName}`
      icon.setAttribute('aria-hidden', 'true')
      container.append(icon)
    }
    const text = document.createElement('span')
    text.textContent = message || ''
    container.append(text)
    container.classList.toggle('text-danger', invalid)
    container.classList.toggle('text-success-emphasis', state === 'saved' && !invalid)
    field.classList.toggle('is-invalid', invalid)
    field.setAttribute('aria-invalid', String(invalid))
    if (invalid) {
      const retry = document.createElement('button')
      retry.type = 'button'
      retry.className = 'profile-autosave-retry btn btn-link btn-sm'
      retry.dataset.profileAutosaveRetry = ''
      retry.textContent = this.messagesValue.retry
      retry.addEventListener('click', () => {
        field.focus()
        this.enqueue(field)
      })
      container.append(retry)
    }
    if (state === 'saved' && !invalid) {
      this.feedbackTimers.set(field, setTimeout(() => this.feedback(field, ''), 3000))
    }
  }

  clearServerErrors(field) {
    this.element.querySelectorAll('[data-profile-autosave-error]').forEach(error => {
      if (error.dataset.profileAutosaveError !== this.attribute(field)) return
      if (error.id) {
        const descriptions = (field.getAttribute('aria-describedby') || '').split(' ').filter(id => id && id !== error.id)
        field.setAttribute('aria-describedby', descriptions.join(' '))
      }
      const group = error.closest('.input-group')
      error.remove()
      if (group && !group.querySelector('.invalid-feedback')) group.classList.remove('has-validation')
    })
  }

  hasUnsavedChanges() {
    return Boolean(this.active || this.queue.size || this.failed.size ||
      this.fields.some(field => this.value(field) !== this.saved(field)))
  }

  beforeUnload(event) {
    if (!this.hasUnsavedChanges()) return
    event.preventDefault()
    event.returnValue = ''
  }

  beforeVisit(event) {
    if (this.hasUnsavedChanges() && !window.confirm(this.messagesValue.leave)) event.preventDefault()
  }

  beforeCache() {
    // A Turbo snapshot must not make an abandoned edit look persisted on return.
    this.fields.forEach(field => {
      const saved = this.saved(field)
      if (field.type === 'checkbox') field.checked = saved
      else field.value = saved
      field.classList.remove('is-invalid')
      field.removeAttribute('aria-invalid')
      this.feedback(field, '')
    })
  }
}
